/**
 * Bot List Component
 *
 * Displays a list of bots with status indicators, filtering, and management actions.
 * Supports tenant-scoped bot listing for multi-tenant environments.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { getApiClient, isSuccess } from '../../api/client';
import { Layout } from '../layout/Layout';

// =============================================================================
// Types
// =============================================================================

/**
 * Bot status enum matching backend
 */
type BotStatus = 'STOPPED' | 'STARTING' | 'RUNNING' | 'STOPPING' | 'ERROR';

/**
 * Bot data structure
 */
interface Bot {
  id: string;
  name: string;
  description: string | null;
  status: BotStatus;
  autoStart: boolean;
  autoRestart: boolean;
  lastStarted: string | null;
  lastStopped: string | null;
  createdAt: string;
  tenantId: string;
  tenantName?: string;
}

/**
 * API response types
 */
interface BotsResponse {
  bots: Bot[];
  total: number;
}

interface BotActionResponse {
  success: boolean;
  message?: string;
}

/**
 * Filter state
 */
type StatusFilter = 'all' | BotStatus;

// =============================================================================
// Constants
// =============================================================================

const STATUS_COLORS: Record<BotStatus, { bg: string; text: string; dot: string }> = {
  RUNNING: { bg: 'bg-green-900/20', text: 'text-green-400', dot: 'bg-green-400' },
  STOPPED: { bg: 'bg-gray-900/20', text: 'text-gray-400', dot: 'bg-gray-400' },
  STARTING: { bg: 'bg-yellow-900/20', text: 'text-yellow-400', dot: 'bg-yellow-400' },
  STOPPING: { bg: 'bg-yellow-900/20', text: 'text-yellow-400', dot: 'bg-yellow-400' },
  ERROR: { bg: 'bg-red-900/20', text: 'text-red-400', dot: 'bg-red-400' },
};

const STATUS_LABELS: Record<BotStatus, string> = {
  RUNNING: 'Running',
  STOPPED: 'Stopped',
  STARTING: 'Starting',
  STOPPING: 'Stopping',
  ERROR: 'Error',
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format a timestamp for display
 */
function formatTimestamp(timestamp: string | null): string {
  if (!timestamp) return 'Never';

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Status badge component
 */
interface StatusBadgeProps {
  status: BotStatus;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const colors = STATUS_COLORS[status];

  return (
    <div
      className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${colors.dot} ${
          status === 'STARTING' || status === 'STOPPING' ? 'animate-pulse' : ''
        }`}
      />
      {STATUS_LABELS[status]}
    </div>
  );
}

/**
 * Bot card component for grid view
 */
interface BotCardProps {
  bot: Bot;
  onStart: (id: string) => void;
  onStop: (id: string) => void;
  onForceStop: (id: string) => void;
  onDelete: (id: string) => void;
  isLoading: boolean;
  showTenant?: boolean;
}

function BotCard({ bot, onStart, onStop, onForceStop, onDelete, isLoading, showTenant }: BotCardProps) {
  const canStart = bot.status === 'STOPPED' || bot.status === 'ERROR';
  const canStop = bot.status === 'RUNNING';
  const canForceStop = bot.status === 'STOPPING';
  const canDelete = bot.status === 'STOPPED' || bot.status === 'ERROR';

  return (
    <div className="rounded-lg bg-discord-darker border border-discord-light p-4 transition-all hover:border-discord-blurple hover:shadow-lg">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <Link
            to={`/bots/${bot.id}/config`}
            className="font-semibold text-white hover:text-discord-blurple transition-colors truncate block"
          >
            {bot.name}
          </Link>
          {bot.description && (
            <p className="mt-1 text-sm text-gray-400 truncate">{bot.description}</p>
          )}
          {showTenant && bot.tenantName && (
            <p className="mt-1 text-xs text-gray-500">Tenant: {bot.tenantName}</p>
          )}
        </div>
        <StatusBadge status={bot.status} />
      </div>

      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-gray-500">
          Last active: {formatTimestamp(bot.lastStarted)}
        </span>

        <div className="flex items-center gap-2">
          {canStart && (
            <button
              onClick={() => onStart(bot.id)}
              disabled={isLoading}
              className="rounded-md bg-green-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Start bot"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                />
              </svg>
            </button>
          )}
          {canStop && (
            <button
              onClick={() => onStop(bot.id)}
              disabled={isLoading}
              className="rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Stop bot"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"
                />
              </svg>
            </button>
          )}
          {canForceStop && (
            <button
              onClick={() => onForceStop(bot.id)}
              disabled={isLoading}
              className="rounded-md bg-red-800 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Force stop bot"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
          <Link
            to={`/bots/${bot.id}/config`}
            className="rounded-md bg-discord-light px-2.5 py-1 text-xs font-medium text-gray-300 hover:bg-discord-blurple hover:text-white transition-colors"
            title="Configure bot"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </Link>
          {canDelete && (
            <button
              onClick={() => onDelete(bot.id)}
              disabled={isLoading}
              className="rounded-md bg-red-900/50 px-2.5 py-1 text-xs font-medium text-red-400 hover:bg-red-600 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Delete bot"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Bot row component for table view
 */
interface BotRowProps {
  bot: Bot;
  onStart: (id: string) => void;
  onStop: (id: string) => void;
  onForceStop: (id: string) => void;
  onDelete: (id: string) => void;
  isLoading: boolean;
  showTenant?: boolean;
}

function BotRow({ bot, onStart, onStop, onForceStop, onDelete, isLoading, showTenant }: BotRowProps) {
  const canStart = bot.status === 'STOPPED' || bot.status === 'ERROR';
  const canStop = bot.status === 'RUNNING';
  const canForceStop = bot.status === 'STOPPING';
  const canDelete = bot.status === 'STOPPED' || bot.status === 'ERROR';

  return (
    <tr className="border-b border-discord-light hover:bg-discord-light/30 transition-colors">
      <td className="px-4 py-3">
        <div className="flex flex-col">
          <Link
            to={`/bots/${bot.id}/config`}
            className="font-medium text-white hover:text-discord-blurple transition-colors"
          >
            {bot.name}
          </Link>
          {bot.description && (
            <span className="text-sm text-gray-400 truncate max-w-xs">{bot.description}</span>
          )}
        </div>
      </td>
      {showTenant && (
        <td className="px-4 py-3 text-sm text-gray-400">{bot.tenantName || '-'}</td>
      )}
      <td className="px-4 py-3">
        <StatusBadge status={bot.status} />
      </td>
      <td className="px-4 py-3 text-sm text-gray-400">
        {formatTimestamp(bot.lastStarted)}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {canStart && (
            <button
              onClick={() => onStart(bot.id)}
              disabled={isLoading}
              className="rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Start
            </button>
          )}
          {canStop && (
            <button
              onClick={() => onStop(bot.id)}
              disabled={isLoading}
              className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Stop
            </button>
          )}
          {canForceStop && (
            <button
              onClick={() => onForceStop(bot.id)}
              disabled={isLoading}
              className="rounded-md bg-red-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Force Stop
            </button>
          )}
          <Link
            to={`/bots/${bot.id}/config`}
            className="rounded-md bg-discord-light px-3 py-1.5 text-xs font-medium text-gray-300 hover:bg-discord-blurple hover:text-white transition-colors"
          >
            Configure
          </Link>
          {canDelete && (
            <button
              onClick={() => onDelete(bot.id)}
              disabled={isLoading}
              className="rounded-md bg-red-900/50 px-3 py-1.5 text-xs font-medium text-red-400 hover:bg-red-600 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Delete
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

/**
 * Status filter buttons component
 */
interface StatusFilterProps {
  value: StatusFilter;
  onChange: (filter: StatusFilter) => void;
  counts: Record<StatusFilter, number>;
}

function StatusFilterButtons({ value, onChange, counts }: StatusFilterProps) {
  const filters: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'RUNNING', label: 'Running' },
    { key: 'STOPPED', label: 'Stopped' },
    { key: 'ERROR', label: 'Error' },
  ];

  return (
    <div className="flex items-center gap-2">
      {filters.map(({ key, label }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            value === key
              ? 'bg-discord-blurple text-white'
              : 'bg-discord-light text-gray-300 hover:text-white'
          }`}
        >
          {label}
          {counts[key] > 0 && (
            <span
              className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs ${
                value === key
                  ? 'bg-white/20'
                  : 'bg-discord-darker'
              }`}
            >
              {counts[key]}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * Empty state component
 */
interface EmptyStateProps {
  filter: StatusFilter;
}

function EmptyState({ filter }: EmptyStateProps) {
  const messages: Record<StatusFilter, { title: string; description: string }> = {
    all: {
      title: 'No bots yet',
      description: 'Create your first bot to get started with DiscordMinstrel',
    },
    RUNNING: {
      title: 'No running bots',
      description: 'Start a bot to see it here',
    },
    STOPPED: {
      title: 'No stopped bots',
      description: 'All your bots are currently running',
    },
    STARTING: {
      title: 'No bots starting',
      description: 'No bots are currently in the starting state',
    },
    STOPPING: {
      title: 'No bots stopping',
      description: 'No bots are currently shutting down',
    },
    ERROR: {
      title: 'No bots in error state',
      description: 'All your bots are operating normally',
    },
  };

  const { title, description } = messages[filter];

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <svg
        className="h-12 w-12 text-gray-500 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      </svg>
      <h3 className="text-lg font-medium text-white">{title}</h3>
      <p className="mt-1 text-sm text-gray-400">{description}</p>
      {filter === 'all' && (
        <Link
          to="/bots/new"
          className="mt-4 inline-flex items-center rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors"
        >
          Create Bot
        </Link>
      )}
    </div>
  );
}

/**
 * Loading skeleton component
 */
function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="flex items-center justify-between">
        <div className="h-8 w-48 bg-discord-light rounded" />
        <div className="h-8 w-32 bg-discord-light rounded" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="rounded-lg bg-discord-darker border border-discord-light p-4">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <div className="h-5 w-32 bg-discord-light rounded" />
                <div className="h-4 w-48 bg-discord-light rounded" />
              </div>
              <div className="h-6 w-20 bg-discord-light rounded-full" />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="h-4 w-24 bg-discord-light rounded" />
              <div className="flex gap-2">
                <div className="h-8 w-8 bg-discord-light rounded" />
                <div className="h-8 w-8 bg-discord-light rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Bot list page component
 *
 * Displays:
 * - List of bots with status indicators
 * - Filter by status
 * - Start/stop controls
 * - Links to bot configuration
 */
export function BotList() {
  useAuth(); // Ensure user is authenticated
  const { currentTenant, effectiveTenantId, isGlobalAdmin } = useTenant();

  const [bots, setBots] = useState<Bot[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  /**
   * Fetch bots from API
   */
  const fetchBots = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const apiClient = getApiClient();

    try {
      const queryParams = effectiveTenantId
        ? `?tenantId=${effectiveTenantId}`
        : '';

      const result = await apiClient.get<BotsResponse>(`/bots${queryParams}`);

      if (isSuccess(result)) {
        setBots(result.data.bots);
      } else {
        setError(result.error.message || 'Failed to fetch bots');
      }
    } catch (err) {
      setError('Failed to load bots. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [effectiveTenantId]);

  /**
   * Start a bot
   */
  const handleStartBot = useCallback(async (botId: string) => {
    setIsActionLoading(true);
    setError(null);

    const apiClient = getApiClient();

    try {
      const result = await apiClient.post<BotActionResponse>(`/bots/${botId}/start`);

      if (isSuccess(result)) {
        // Update bot status locally for immediate feedback
        setBots((prev) =>
          prev.map((bot) =>
            bot.id === botId ? { ...bot, status: 'STARTING' as BotStatus } : bot
          )
        );
        // Refresh after a delay to get accurate status
        setTimeout(() => void fetchBots(), 2000);
      } else {
        setError(result.error.message || 'Failed to start bot');
      }
    } catch (err) {
      setError('Failed to start bot. Please try again.');
    } finally {
      setIsActionLoading(false);
    }
  }, [fetchBots]);

  /**
   * Stop a bot
   */
  const handleStopBot = useCallback(async (botId: string) => {
    setIsActionLoading(true);
    setError(null);

    const apiClient = getApiClient();

    try {
      const result = await apiClient.post<BotActionResponse>(`/bots/${botId}/stop`);

      if (isSuccess(result)) {
        // Update bot status locally for immediate feedback
        setBots((prev) =>
          prev.map((bot) =>
            bot.id === botId ? { ...bot, status: 'STOPPING' as BotStatus } : bot
          )
        );
        // Refresh after a delay to get accurate status
        setTimeout(() => void fetchBots(), 2000);
      } else {
        setError(result.error.message || 'Failed to stop bot');
      }
    } catch (err) {
      setError('Failed to stop bot. Please try again.');
    } finally {
      setIsActionLoading(false);
    }
  }, [fetchBots]);

  /**
   * Force stop a bot stuck in 'stopping' state
   */
  const handleForceStopBot = useCallback(async (botId: string) => {
    setIsActionLoading(true);
    setError(null);

    const apiClient = getApiClient();

    try {
      const result = await apiClient.post<BotActionResponse>(`/bots/${botId}/force-stop`);

      if (isSuccess(result)) {
        setBots((prev) =>
          prev.map((bot) =>
            bot.id === botId ? { ...bot, status: 'STOPPED' as BotStatus } : bot
          )
        );
        setTimeout(() => void fetchBots(), 1000);
      } else {
        setError(result.error.message || 'Failed to force stop bot');
      }
    } catch (err) {
      setError('Failed to force stop bot. Please try again.');
    } finally {
      setIsActionLoading(false);
    }
  }, [fetchBots]);

  /**
   * Delete a bot
   */
  const handleDeleteBot = useCallback(async (botId: string) => {
    const bot = bots.find((b) => b.id === botId);
    const botName = bot?.name || 'this bot';

    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete "${botName}"? This action cannot be undone.`)) {
      return;
    }

    setIsActionLoading(true);
    setError(null);

    const apiClient = getApiClient();

    try {
      const result = await apiClient.delete<BotActionResponse>(`/bots/${botId}`);

      if (isSuccess(result)) {
        // Remove bot from local state immediately
        setBots((prev) => prev.filter((b) => b.id !== botId));
      } else {
        setError(result.error.message || 'Failed to delete bot');
      }
    } catch (err) {
      setError('Failed to delete bot. Please try again.');
    } finally {
      setIsActionLoading(false);
    }
  }, [bots]);

  /**
   * Initial data fetch
   */
  useEffect(() => {
    void fetchBots();
  }, [fetchBots]);

  /**
   * Calculate status counts
   */
  const statusCounts = useMemo(() => {
    const counts: Record<StatusFilter, number> = {
      all: bots.length,
      RUNNING: 0,
      STOPPED: 0,
      STARTING: 0,
      STOPPING: 0,
      ERROR: 0,
    };

    bots.forEach((bot) => {
      counts[bot.status]++;
    });

    return counts;
  }, [bots]);

  /**
   * Filter and search bots
   */
  const filteredBots = useMemo(() => {
    let filtered = bots;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((bot) => bot.status === statusFilter);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (bot) =>
          bot.name.toLowerCase().includes(query) ||
          (bot.description && bot.description.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [bots, statusFilter, searchQuery]);

  /**
   * Show tenant column for global admins viewing all tenants
   */
  const showTenant = isGlobalAdmin && !currentTenant;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Bots</h1>
            <p className="mt-1 text-gray-400">
              {currentTenant
                ? `Manage bots for ${currentTenant.name}`
                : isGlobalAdmin
                  ? 'Manage all bots across tenants'
                  : 'Manage your Discord bots'}
            </p>
          </div>
          <Link
            to="/bots/new"
            className="inline-flex items-center gap-2 rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Bot
          </Link>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="rounded-md bg-red-900/50 border border-red-500 p-4">
            <div className="flex items-start">
              <svg
                className="mr-3 h-5 w-5 flex-shrink-0 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-red-200">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-3 text-sm font-medium text-red-400 hover:text-red-300"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {/* Filters and Controls */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <StatusFilterButtons
                value={statusFilter}
                onChange={setStatusFilter}
                counts={statusCounts}
              />

              <div className="flex items-center gap-3">
                {/* Search Input */}
                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search bots..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-48 rounded-md border border-discord-light bg-discord-darker py-2 pl-10 pr-4 text-sm text-white placeholder-gray-500 focus:border-discord-blurple focus:outline-none focus:ring-1 focus:ring-discord-blurple"
                  />
                </div>

                {/* View Toggle */}
                <div className="flex rounded-md bg-discord-light">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`rounded-l-md px-3 py-2 text-sm transition-colors ${
                      viewMode === 'grid'
                        ? 'bg-discord-blurple text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                    title="Grid view"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`rounded-r-md px-3 py-2 text-sm transition-colors ${
                      viewMode === 'table'
                        ? 'bg-discord-blurple text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                    title="Table view"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 10h16M4 14h16M4 18h16"
                      />
                    </svg>
                  </button>
                </div>

                {/* Refresh Button */}
                <button
                  onClick={() => void fetchBots()}
                  disabled={isLoading}
                  className="rounded-md bg-discord-light p-2 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                  title="Refresh"
                >
                  <svg
                    className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Bot List */}
            {filteredBots.length === 0 ? (
              <EmptyState filter={statusFilter} />
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filteredBots.map((bot) => (
                  <BotCard
                    key={bot.id}
                    bot={bot}
                    onStart={handleStartBot}
                    onStop={handleStopBot}
                    onForceStop={handleForceStopBot}
                    onDelete={handleDeleteBot}
                    isLoading={isActionLoading}
                    showTenant={showTenant}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-discord-light overflow-hidden">
                <table className="w-full">
                  <thead className="bg-discord-darker">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Name
                      </th>
                      {showTenant && (
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                          Tenant
                        </th>
                      )}
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Last Active
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-discord-dark">
                    {filteredBots.map((bot) => (
                      <BotRow
                        key={bot.id}
                        bot={bot}
                        onStart={handleStartBot}
                        onStop={handleStopBot}
                        onForceStop={handleForceStopBot}
                        onDelete={handleDeleteBot}
                        isLoading={isActionLoading}
                        showTenant={showTenant}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Summary */}
            <div className="flex items-center justify-between text-sm text-gray-400">
              <span>
                Showing {filteredBots.length} of {bots.length} bots
              </span>
              <span>
                {statusCounts.RUNNING} running · {statusCounts.STOPPED} stopped
                {statusCounts.ERROR > 0 && ` · ${statusCounts.ERROR} errors`}
              </span>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

export default BotList;
